import { isValidAddress } from 'components/forms/address/AddressForm';
import { Address, CurrentQuoteState, LineItem, PhoneInput, ShipmentSchedule } from 'types';
import { LOAD_CHECKOUT_DATA_PARAM } from 'utils/constants';

const checkoutDataKey = 'oe.ordering.storedCheckoutFieldsData';

export const saveCheckoutDataInLocalStorage = (checkoutData): void => {
  const { transactionId } = checkoutData;
  const existingCheckoutData = JSON.parse(localStorage.getItem(checkoutDataKey) || '{}');
  let updatedData;

  if (transactionId === existingCheckoutData.transactionId) {
    updatedData = {
      ...existingCheckoutData,
      ...checkoutData,
    };
  } else {
    updatedData = checkoutData;
  }

  localStorage.setItem(checkoutDataKey, JSON.stringify(updatedData));
};

export const removeCheckoutDataFromLocalStorage = () => {
  localStorage.removeItem(checkoutDataKey);
};

const mapPhoneNumberFromQuote = (phone): PhoneInput => {
  return {
    countryCode: phone.countryCode,
    areaCode: phone.areaCode,
    number: phone.number,
    extension: phone.extension,
  };
};

const mapPhoneNumberFromStorage = (contact): PhoneInput => {
  return {
    countryCode: contact.phoneCountry,
    areaCode: contact.phoneArea,
    number: contact.phoneNumber,
    extension: contact.phoneExtension,
  };
};

const mapShippingContactFromQuote = (quote: CurrentQuoteState) => ({
  firstName: quote.shippingContactFirstName || '',
  lastName: quote.shippingContactLastName || '',
  email: quote.shippingContactEmail || '',
  confirmEmail: quote.shippingContactEmail || '',
  phoneNumber: mapPhoneNumberFromQuote(quote.shippingContactPhone),
});

const mapShippingContactFromStorage = (contact) => {
  if (!contact) {
    return undefined;
  }

  return {
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    confirmEmail: contact.emailConfirm,
    phoneNumber: mapPhoneNumberFromStorage(contact),
  };
};

const getShippingInfoFromQuote = (quote: CurrentQuoteState) => ({
  packingInstructions: quote.packingInstructions || '',
  shippingInstructions: quote.shippingInstructions || '',
  shipmentSchedule: formatShipmentScheduleForCheckout(
    quote.shipmentSchedule || [],
    quote.items || []
  ),
});

const getShippingInfoFromStorage = (shippingInfo, quote: CurrentQuoteState) => {
  if (shippingInfo) {
    return {
      packingInstructions: shippingInfo.packingInstructions,
      shippingInstructions: shippingInfo.shippingInstructions,
      shipmentSchedule: shippingInfo.shipmentSchedule,
    };
  }

  return {
    // if no shipping info saved in storage, use default instructions from quote
    packingInstructions: quote.packingInstructions || '',
    shippingInstructions: quote.shippingInstructions || '',
    shipmentSchedule: undefined,
  };
};

export type CheckoutSavedData = {
  shippingAddress?: Address;
  billingAddress?: Address & {
    lesingAgent: boolean;
  };
  shippingContact?: {
    phoneNumber?: PhoneInput;
    email: string;
    confirmEmail: string;
    lastName: string;
    firstName: string;
  };
  shippingInfo:
    | {
        shipmentSchedule: any;
        packingInstructions: string | undefined;
        shippingInstructions: string | undefined;
      }
    | undefined;
  poNumber: string;
  poFileAttachmentName: string;
  lesingAgent?: boolean;
  csapEnrolled?: string;
};

export const readCheckoutSavedData = (
  transactionId,
  currentQuote = {} as CurrentQuoteState
): CheckoutSavedData => {
  const localStorageData = getSavedCheckoutData(transactionId);
  const urlParams = new URLSearchParams(window.location.search);
  const loadShippingDataFromQuote = urlParams.get(LOAD_CHECKOUT_DATA_PARAM) === 'true';

  const {
    shippingAddress,
    shippingInfo,
    shippingContact,
    billingAddress,
    poNumber,
    lesingAgent,
    poFileAttachmentName,
    csapEnrolled,
  } = localStorageData;

  const baseData = {
    billingAddress,
    poNumber,
    lesingAgent,
    poFileAttachmentName,
    csapEnrolled,
  };

  if (loadShippingDataFromQuote) {
    return {
      ...baseData,
      shippingAddress: currentQuote.shippingAddress,
      shippingInfo: getShippingInfoFromQuote(currentQuote),
      shippingContact: mapShippingContactFromQuote(currentQuote),
    };
  }

  return {
    ...baseData,
    shippingAddress: shippingAddress
      ? {
          ...shippingAddress,
          addressId: shippingAddress.id,
        }
      : undefined,
    shippingInfo: getShippingInfoFromStorage(shippingInfo, currentQuote),
    shippingContact: mapShippingContactFromStorage(shippingContact),
  };
};

export const getSavedCheckoutData = (transactionId) => {
  const localStorageData = JSON.parse(localStorage.getItem(checkoutDataKey) || '{}');

  if (transactionId === localStorageData.transactionId) {
    return localStorageData;
  } else {
    return {};
  }
};

export const isAllShipmentLinesZero = (shipment) => {
  return shipment.lines.every((line) => line.quantity === 0);
};

export const getShipmentQuantityError = (shipments) => {
  const errors = {};
  const partNumberTotals: {
    partNumber: string;
    quantity: number;
    totalQuantity: number;
  }[] = [];

  shipments.forEach((shipment) => {
    shipment.lines.forEach((line) => {
      const existingData = partNumberTotals.find(
        (partNumberTotal) => partNumberTotal.partNumber === line.partNumber
      );

      if (existingData) {
        existingData.quantity += line.quantity;
      } else {
        partNumberTotals.push({
          partNumber: line.partNumber,
          quantity: line.quantity,
          totalQuantity: line.totalQuantity,
        });
      }
    });
  });

  partNumberTotals.forEach((partNumberTotal) => {
    const diffInQuantity = partNumberTotal.quantity - partNumberTotal.totalQuantity;
    let error;

    if (diffInQuantity > 0) {
      error = `${diffInQuantity} over total`;
    } else if (diffInQuantity < 0) {
      error = `${-diffInQuantity} under total`;
    }

    if (error) {
      errors[partNumberTotal.partNumber] = error;
    }
  });

  return errors;
};

export const validFavAddressDetails = (address, favorites): boolean => {
  const { address1, address2, city, state, zipcode, country } = address;
  const isValid = isValidAddress(address).isValid;

  return (
    isValid &&
    !favorites.some((favorite) => {
      const favAddress = favorite.address;

      return (
        favAddress.address1 === address1 &&
        (favAddress.address2 ?? '') === address2 &&
        favAddress.city === city &&
        favAddress.state === state &&
        favAddress.zipcode === zipcode &&
        favAddress.country === country
      );
    })
  );
};

export const formatShipmentScheduleForCheckout = (
  shipmentSchedule: ShipmentSchedule[],
  items: LineItem[]
) => {
  if (!shipmentSchedule || shipmentSchedule.length === 0) {
    return [];
  }

  const partNumberToLineItemMap: { [partNumber: string]: LineItem & { totalQuantity: number } } =
    {};

  items?.forEach((item) => {
    if (partNumberToLineItemMap[item.partNumber]) {
      partNumberToLineItemMap[item.partNumber] = {
        ...item,
        totalQuantity: partNumberToLineItemMap[item.partNumber].totalQuantity + item.quantity,
      };
    } else {
      partNumberToLineItemMap[item.partNumber] = { ...item, totalQuantity: item.quantity };
    }
  });

  return shipmentSchedule.map((schedule) => {
    return {
      ...schedule,
      lines: schedule.lines.map((line) => {
        return {
          partNumber: line.partNumber,
          quantity: line.quantity,
          totalQuantity: partNumberToLineItemMap[line.partNumber]?.totalQuantity || 0,
          partName: partNumberToLineItemMap[line.partNumber]?.partName || '',
        };
      }),
    };
  });
};

export const formatShipmentScheduleForQuote = (shipmentSchedule) => {
  return (
    shipmentSchedule.map((shippingSchedule) => ({
      shipDate: shippingSchedule.shipDate,
      lines: shippingSchedule.lines.map((line) => ({
        partNumber: line.partNumber,
        quantity: line.quantity,
      })),
    })) || []
  );
};

export const mapShippingCheckoutDataToQuote = ({
  shippingAddress,
  shippingContact,
  shippingInfo,
}) => {
  const shipmentSchedules = formatShipmentScheduleForQuote(shippingInfo.shipmentSchedule);

  return {
    shippingAddress: {
      name: shippingAddress?.name,
      address1: shippingAddress?.address1,
      address2: shippingAddress?.address2,
      city: shippingAddress?.city,
      state: shippingAddress?.state,
      zipcode: shippingAddress?.zipcode,
      country: shippingAddress?.country,
    },
    requestedShippingType: shipmentSchedules.length > 1 ? 'Multiple Shipments' : 'Specified Date',
    requestedShippingDate: shipmentSchedules.length === 1 ? shipmentSchedules[0].shipDate : null,
    multipleShipments:
      shipmentSchedules.length > 1
        ? shipmentSchedules.reduce((schedule1, schedule2) => {
            return `${schedule1}\n${schedule2.shipDate}${schedule2.lines.reduce((line1, line2) => {
              return `${line1}-${line2.partNumber}x${line2.quantity}`;
            }, '')}`;
          }, '')
        : null,
    shippingContactFirstName: shippingContact?.firstName,
    shippingContactLastName: shippingContact?.lastName,
    shippingContactPhone: {
      countryCode: shippingContact?.phoneNumber?.countryCode
        ? parseInt(shippingContact?.phoneNumber?.countryCode + '')
        : null,
      areaCode: shippingContact?.phoneNumber?.areaCode
        ? parseInt(shippingContact?.phoneNumber?.areaCode + '')
        : null,
      number: shippingContact?.phoneNumber?.number,
      extension: shippingContact?.phoneNumber?.extension,
    },
    shippingContactEmail: shippingContact?.email,
    shipmentSchedule: shipmentSchedules,
    packingInstructions: shippingInfo.packingInstructions,
    shippingInstructions: shippingInfo.shippingInstructions,
  };
};

export const mapCheckoutDataToQuote = ({
  csapEnrolled,
  shippingAddress,
  shippingContact,
  billingAddress,
  recipients,
  lesingAgent,
  poNumber,
  shippingInfo,
}) => {
  return {
    ...mapShippingCheckoutDataToQuote({
      shippingAddress,
      shippingContact,
      shippingInfo,
    }),
    confirmationRecipients: recipients,
    billingAddress: {
      name: billingAddress?.name,
      address1: billingAddress?.address1,
      address2: billingAddress?.address2,
      city: billingAddress?.city,
      state: billingAddress?.state,
      zipcode: billingAddress?.zipcode,
      country: billingAddress?.country,
    },
    lesingAgent,
    poNumber,
    csapEnrolled,
  };
};
